import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import { Checkbox, FadingScrollable, cn } from '@renderer/chat'
import { useFetchGoogleCalContacts } from '@renderer/hooks/use-google-cal-contacts'
import { Button, Combobox, Dialog } from '@highlight/ui'
import { AudioNote } from '@highlight/common'
import { useShareAudioNote } from '@renderer/hooks/useShareAudioNote'
import { Participants } from '@renderer/overlay/components/participants'
import { Send, Clock, UserCheck, UserX, User } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import emailSharer from '@assets/screenshots/email-sharer.png'
import { groupEmailsByDomain, sortDomains, formatDomainName } from '@renderer/utils/email-utils'
import { isMeetingNote, noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'

function AudioNoteEmailSharer({ audioNote }: { audioNote: AudioNote }) {
  const { sendEmail, shareAudioNote } = useShareAudioNote({
    audioNote: audioNote || null,
    meetingAppName: audioNote?.meeting?.app?.name,
    eventAction: 'email',
  })
  const [customEmails, setCustomEmails] = useState<string[]>([])
  const [showEmailSharer] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [sendingEmails, setSendingEmails] = useState<Set<string>>(new Set())
  const [optimisticPending, setOptimisticPending] = useState<Set<string>>(new Set())

  // Get shared recipients from the audio note data - this is the source of truth
  const sharedRecipients = useMemo(() => audioNote?.sharedRecipients || [], [audioNote?.sharedRecipients])

  // Clear optimistic pending when backend confirms the status
  React.useEffect(() => {
    const backendEmails = new Set(sharedRecipients.map((r) => r.email))
    setOptimisticPending((prev) => {
      const next = new Set(prev)
      let changed = false
      for (const email of prev) {
        if (backendEmails.has(email)) {
          next.delete(email)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [sharedRecipients])

  // Only use backend data and custom emails - don't use stale local state
  const attendees = useMemo(
    () =>
      Array.from(
        new Set([...(audioNote?.meeting?.attendees || []), ...customEmails, ...sharedRecipients.map((r) => r.email)]),
      ).filter(Boolean),
    [audioNote?.meeting?.attendees, customEmails, sharedRecipients],
  )

  const [query, setQuery] = useState('')
  const { data: contactsData, isLoading: isContactsLoading } = useFetchGoogleCalContacts()
  const [selected, setSelected] = useState<Record<string, boolean>>({})

  // Sync selection state when attendees change - auto-select new attendees not in backend
  React.useEffect(() => {
    setSelected((prev) => {
      const next = { ...prev }
      for (const email of attendees) {
        if (!(email in next)) {
          const alreadyShared = sharedRecipients.some((r) => r.email === email)
          next[email] = !alreadyShared
        }
      }
      return next
    })
  }, [attendees, sharedRecipients])

  const isValidEmail = useCallback((value: string) => {
    const email = value.trim().toLowerCase()
    // Simple, practical email regex; avoids exotic cases while catching common formats
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    return emailRegex.test(email)
  }, [])

  const emailExistsAnywhere = useCallback(
    (email: string) => {
      const lower = email.toLowerCase()
      return attendees.some((a) => a.toLowerCase() === lower) || customEmails.some((c) => c.toLowerCase() === lower)
    },
    [attendees, customEmails],
  )

  const handleAddEmail = useCallback(
    (raw: string) => {
      const email = raw.trim().toLowerCase()
      if (!isValidEmail(email)) return
      if (emailExistsAnywhere(email)) return
      setCustomEmails((prev) => [...prev, email])
      const alreadyShared = sharedRecipients.some((r) => r.email === email)
      setSelected((prev) => ({ ...prev, [email]: !alreadyShared }))
      setQuery('')
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [emailExistsAnywhere, isValidEmail, sharedRecipients],
  )

  const grouped = useMemo(() => {
    return groupEmailsByDomain(attendees)
  }, [attendees])

  const domains = useMemo(() => sortDomains(Object.keys(grouped)), [grouped])

  const setAll = (emails: string[], value: boolean) => {
    setSelected((prev) => {
      const next = { ...prev }
      for (const e of emails) next[e] = value
      return next
    })
  }

  const sentSet = useMemo(() => {
    const sent = new Set<string>()
    sharedRecipients.forEach((r) => sent.add(r.email))
    return sent
  }, [sharedRecipients])

  const selectedList = useMemo(() => {
    return Object.entries(selected)
      .filter(([email, value]) => value && !sentSet.has(email))
      .map(([email]) => email)
  }, [selected, sentSet])

  const filteredContacts = useMemo(() => {
    if (!contactsData?.attendees) return []
    const lowerExisting = new Set(attendees.map((e) => e.toLowerCase()))
    return contactsData.attendees
      .filter((email) => {
        const matches = query === '' || email.toLowerCase().includes(query.toLowerCase())
        const notAlready = !lowerExisting.has(email.toLowerCase())
        return matches && notAlready
      })
      .slice(0, 10)
  }, [contactsData?.attendees, query, attendees])

  // Create items list for Combobox - combine "add new" option with filtered contacts
  const comboboxItems = useMemo(() => {
    const showAddOption = query.trim() && isValidEmail(query.trim()) && !emailExistsAnywhere(query.trim())

    if (showAddOption) {
      return [`add:${query.trim()}`, ...filteredContacts]
    }
    return filteredContacts
  }, [query, filteredContacts, isValidEmail, emailExistsAnywhere])

  const handleResendToEmail = useCallback(
    async (email: string) => {
      try {
        // Mark this email as sending
        setSendingEmails((prev) => new Set([...prev, email]))

        // Send email
        await sendEmail(audioNote, [email])

        // Update backend
        await shareAudioNote(audioNote, {
          recipientEmail: email,
          message: `Check out these ${noteNoun(isMeetingNote(audioNote), { plural: true })} from ${audioNote.title || 'our meeting'}`,
        })

        // Show optimistic pending
        setOptimisticPending(new Set([email]))
        setSendingEmails(new Set())
      } catch (error) {
        console.error('Failed to resend to email:', error)
        setSendingEmails(new Set())
      }
    },
    [sendEmail, shareAudioNote, audioNote],
  )

  const handleSendCustom = useCallback(async () => {
    if (selectedList.length === 0) return

    try {
      setIsSending(true)
      // Mark these emails as sending immediately
      setSendingEmails(new Set(selectedList))

      // Send email first
      await sendEmail(audioNote, selectedList)

      // Then update backend with sharing data for each email in parallel
      await Promise.all(
        selectedList.map((email) =>
          shareAudioNote(audioNote, {
            recipientEmail: email,
            message: `Check out these ${noteNoun(isMeetingNote(audioNote), { plural: true })} from ${audioNote.title || 'our meeting'}`,
          }),
        ),
      )

      // Immediately show optimistic "pending" status
      setOptimisticPending(new Set(selectedList))
      setSendingEmails(new Set())
    } catch (error) {
      console.error('Failed to share audio note:', error)
      setSendingEmails(new Set())
    } finally {
      setIsSending(false)
    }
  }, [sendEmail, shareAudioNote, audioNote, selectedList])

  const selectedCount = selectedList.length

  const getRecipientStatus = useCallback(
    (email: string) => {
      const sharedRecipient = sharedRecipients.find((r) => r.email === email)

      // Status configurations
      const statusConfig = {
        pending: { icon: <Clock size={20} className="text-foreground-secondary" />, text: 'Pending', disabled: true },
        accepted: { icon: <UserCheck size={20} className="text-green/60" />, text: 'Accepted', disabled: true },
        declined: {
          icon: <UserX size={20} className="text-red/60" />,
          text: 'Declined',
          disabled: false,
          canResend: true,
        },
        direct: { icon: <User size={20} className="text-blue/60" />, text: 'Shared', disabled: true },
      }

      // If currently sending this specific email, show sending status
      if (sendingEmails.has(email)) {
        return {
          icon: <Clock size={20} className="text-foreground-secondary" />,
          text: 'Sending...',
          disabled: true,
          canResend: false,
        }
      }

      // Show optimistic pending if we just sent it and backend hasn't confirmed yet
      if (optimisticPending.has(email)) {
        return {
          icon: <Clock size={20} className="text-foreground-secondary" />,
          text: 'Pending',
          disabled: true,
          canResend: false,
        }
      }

      // Sharing status from backend (source of truth)
      if (sharedRecipient) {
        const config = statusConfig[sharedRecipient.status]
        return config ? { ...config } : { icon: null, text: '', disabled: false, canResend: false }
      }

      return { icon: null, text: '', disabled: false, canResend: false }
    },
    [sharedRecipients, sendingEmails, optimisticPending],
  )

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-3">
      <div className="relative flex items-center gap-2">
        <Combobox
          items={comboboxItems}
          value={null}
          onValueChange={(newValue) => {
            const value = newValue as string
            if (value?.startsWith('add:')) {
              const email = value.replace('add:', '')
              handleAddEmail(email)
            } else if (value) {
              handleAddEmail(value)
            }
          }}
          inputValue={query}
          onInputValueChange={setQuery}
          onOpenChange={(open) => {
            if (!open) {
              // Clear query when closing if it's not a valid email
              if (!isValidEmail(query) || emailExistsAnywhere(query)) {
                setQuery('')
              }
            }
          }}
          className="w-full"
        >
          <Combobox.Input
            placeholder="Add email to send link to"
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && isValidEmail(query) && !emailExistsAnywhere(query)) {
                e.preventDefault()
                handleAddEmail(query)
              }
            }}
            className="bg-light-5 h-10 w-full rounded-xl text-sm"
          />

          <Combobox.Portal>
            <Combobox.Positioner sideOffset={4} className="z-[9999]">
              <Combobox.Popup className="bg-tertiary z-[9999]">
                {isContactsLoading ? (
                  <div className="text-light/60 px-3 py-2 text-sm">Loading contacts...</div>
                ) : (
                  <>
                    <Combobox.Empty>No matching emails found.</Combobox.Empty>

                    <Combobox.List>
                      {(item: string) => {
                        if (item.startsWith('add:')) {
                          const email = item.replace('add:', '')
                          return (
                            <Combobox.Item
                              key={item}
                              value={item}
                              className="bg-light/10 rounded-xl font-medium"
                              showIndicator={false}
                            >
                              <span className="text-light/60 font-normal">Add</span>
                              {` "${email}"`}
                            </Combobox.Item>
                          )
                        }
                        return (
                          <Combobox.Item key={item} value={item} showIndicator={false}>
                            {item}
                          </Combobox.Item>
                        )
                      }}
                    </Combobox.List>
                  </>
                )}
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox>
      </div>

      <FadingScrollable className="h-[25vh] min-h-[25vh] overflow-y-auto rounded">
        {domains.map((domain) => {
          const emails = grouped[domain]
          const domainSelectable = emails.filter((e) => {
            const status = getRecipientStatus(e)
            return !status.disabled
          })
          const domainSelected = domainSelectable.filter((e) => selected[e]).length
          const allSelected = domainSelectable.length > 0 && domainSelected === domainSelectable.length

          return (
            <div key={domain} className="flex flex-col gap-2 py-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 px-3">
                  <div className="text-light-40 text-sm">{formatDomainName(domain)}</div>
                </div>
                {domainSelectable.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="xs" onClick={() => setAll(domainSelectable, !allSelected)}>
                      {allSelected ? 'Unselect all' : 'Select all'}
                    </Button>
                  </div>
                )}
              </div>
              <div className="mb-3 grid grid-cols-1 overflow-hidden rounded-xl border">
                {emails.map((email) => {
                  const status = getRecipientStatus(email)
                  return (
                    <div
                      key={email}
                      className={cn(
                        'border-light-2 flex h-10 items-center justify-between border-b px-2',
                        status.disabled && !status.canResend ? 'opacity-50' : 'hover:bg-light/5',
                        !status.disabled || status.canResend ? 'cursor-pointer' : '',
                      )}
                      onClick={
                        status.disabled && !status.canResend
                          ? undefined
                          : status.canResend
                            ? undefined
                            : () => setSelected((prev) => ({ ...prev, [email]: !prev[email] }))
                      }
                    >
                      <div className="flex items-center gap-2">
                        {status.icon || (
                          <Checkbox
                            disabled={status.disabled}
                            className="pointer-events-none rounded-[5px]"
                            checked={!!selected[email] && !status.disabled}
                            onCheckedChange={(v: boolean) => setSelected((prev) => ({ ...prev, [email]: !!v }))}
                          />
                        )}
                        <span
                          className={twMerge(
                            'h-4 text-sm',
                            !!selected[email] && !status.disabled ? 'text-light/80' : 'text-light/50',
                          )}
                        >
                          {email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {status.canResend && (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleResendToEmail(email)
                            }}
                            disabled={sendingEmails.has(email)}
                          >
                            Send again
                          </Button>
                        )}
                        <span className="text-light/60 min-w-[80px] text-right text-xs">{status.text}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
        {domains.length === 0 && (
          <div className="flex min-h-[25vh] flex-col items-center justify-center gap-3 p-8 text-center">
            <div className="bg-light/5 rounded-full p-3 pb-2">
              <Send size={24} className="text-light/40" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-light text-md mt-3.5 font-medium">No recipients added</p>
              <p className="text-light/50 mt-1 text-[12px]">Type an email address above to add your first recipient</p>
            </div>
          </div>
        )}
      </FadingScrollable>

      <Dialog.Footer className="justify-between p-0 px-2">
        <div>
          {selectedCount > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="">
                <Participants participants={selectedList} size="sm" />
              </div>

              <span className="text-light-60 font-base mr-auto">
                Email {selectedCount} {selectedCount === 1 ? 'person' : 'people'}
              </span>
            </div>
          )}
        </div>

        <Button
          variant="primary"
          disabled={selectedCount === 0 || isSending}
          loading={isSending}
          left={<Send />}
          onClick={handleSendCustom}
        >
          Send link
        </Button>
      </Dialog.Footer>
      <AnimatePresence initial={false} mode="popLayout">
        {showEmailSharer && (
          <motion.img
            key={emailSharer}
            src={emailSharer}
            alt="Email sharer"
            className="h-auto w-full rounded-xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

AudioNoteEmailSharer.displayName = 'AudioNoteEmailSharer'
export default AudioNoteEmailSharer
